import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import roleMessagesAPI from '../services/roleMessages';

const RoleMessagesContext = createContext(null);
const ELEVATED_ROLES = new Set(['SuperAdmin', 'Admin', 'GlobalSupport', 'AdminEvent']);
// BoothAdmin shares Recruiter page instructions (admin UI only exposes Recruiter screens).
const INSTRUCTION_ROLE_ALIASES = {
  BoothAdmin: 'Recruiter'
};
const SCREEN_ROLE_MAP = {
  'my-account': 'JobSeeker',
  'delete-account': 'JobSeeker',
  'edit-profile': 'JobSeeker',
  'view-profile': 'JobSeeker',
  'upcoming-events': 'JobSeeker',
  'event-registration': 'JobSeeker',
  'registered-events': 'JobSeeker',
  'registered-event-detail': 'JobSeeker',
  survey: 'JobSeeker',
  'resume-builder': 'JobSeeker',
  dashboard: 'Recruiter',
  'meeting-queue': 'Recruiter',
  'meeting-records': 'Recruiter',
  'jobseeker-interests': 'Recruiter',
  'interpreter-dashboard': 'Interpreter',
  troubleshooting: 'Interpreter',
  instructions: 'Interpreter'
};

const normalizeMessages = (payload) => {
  if (typeof payload === 'object' && payload !== null && !Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload)) {
    const messagesMap = {};
    payload.forEach((msg) => {
      if (!msg.screen) return;
      if (!messagesMap[msg.screen]) {
        messagesMap[msg.screen] = {};
      }
      messagesMap[msg.screen][msg.messageKey || 'default'] = msg.content;
    });
    return messagesMap;
  }

  return {};
};

export function RoleMessagesProvider({ children }) {
  const { user } = useAuth();
  const [messagesByRole, setMessagesByRole] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getScreenMessages = useCallback((role, screen) => {
    if (!role) return null;
    const primary = messagesByRole[role]?.[screen];
    if (primary && typeof primary === 'object') return primary;

    const aliasRole = INSTRUCTION_ROLE_ALIASES[role];
    const aliased = aliasRole ? messagesByRole[aliasRole]?.[screen] : null;
    return aliased && typeof aliased === 'object' ? aliased : null;
  }, [messagesByRole]);

  const resolveTargetRole = useCallback((screen, explicitRole) => {
    if (explicitRole) return explicitRole;
    // Prefer the viewer's own role so each role sees its own configured
    // instruction. Fall back to the legacy screen→role map (used for elevated
    // users previewing pages they don't own).
    if (user?.role && getScreenMessages(user.role, screen)) return user.role;
    if (SCREEN_ROLE_MAP[screen]) return SCREEN_ROLE_MAP[screen];
    return user?.role || null;
  }, [user?.role, getScreenMessages]);

  // Fetch role messages for current user role (and audience roles for elevated users).
  const fetchMessages = useCallback(async () => {
    if (!user?.role) {
      setMessagesByRole({});
      return;
    }

    setLoading(true);
    setError(null);
    // Always include the viewer's own role so role-specific banners resolve,
    // plus the audience roles elevated users may preview.
    const audienceRoles = ELEVATED_ROLES.has(user.role)
      ? ['JobSeeker', 'Recruiter', 'Interpreter']
      : [];
    const aliasRole = INSTRUCTION_ROLE_ALIASES[user.role];
    const rolesToFetch = Array.from(new Set([...audienceRoles, user.role, aliasRole].filter(Boolean)));

    try {
      const settled = await Promise.allSettled(
        rolesToFetch.map((role) => roleMessagesAPI.getMessagesByRole(role))
      );
      const nextMessagesByRole = {};
      const failures = [];

      rolesToFetch.forEach((role, index) => {
        const result = settled[index];
        if (result.status === 'fulfilled') {
          const response = result.value;
          nextMessagesByRole[role] = response?.success ? normalizeMessages(response.messages) : {};
          return;
        }

        nextMessagesByRole[role] = {};
        const err = result.reason;
        const status = err?.response?.status;
        const data = err?.response?.data;
        console.error(
          `Error fetching role messages for ${role}:`,
          status ? { status, data, message: err.message } : err
        );
        failures.push(err);
      });

      setMessagesByRole(nextMessagesByRole);
      if (failures.length > 0) {
        setError(failures[0]);
      }
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      console.error(
        'Error fetching role messages:',
        status ? { status, data, message: err.message } : err
      );
      setError(err);
      setMessagesByRole({});
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  // Fetch messages when user role changes
  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Get a specific message by screen and messageKey, with optional role override.
  const getMessage = useCallback((screen, messageKey, role = null) => {
    const targetRole = resolveTargetRole(screen, role);
    if (!targetRole) return null;
    const screenMessages = getScreenMessages(targetRole, screen);
    if (!screenMessages) {
      return null;
    }

    if (messageKey && screenMessages[messageKey]) {
      return screenMessages[messageKey];
    }

    if (screenMessages.default) {
      return screenMessages.default;
    }

    const firstMessage = Object.values(screenMessages)[0];
    return firstMessage || null;
  }, [getScreenMessages, resolveTargetRole]);

  // Refresh messages (useful after admin updates)
  const refreshMessages = useCallback(() => {
    fetchMessages();
  }, [fetchMessages]);

  const value = {
    messages: messagesByRole[user?.role] || {},
    messagesByRole,
    loading,
    error,
    getMessage,
    refreshMessages
  };

  return (
    <RoleMessagesContext.Provider value={value}>
      {children}
    </RoleMessagesContext.Provider>
  );
}

export function useRoleMessages() {
  const context = useContext(RoleMessagesContext);
  if (!context) {
    throw new Error('useRoleMessages must be used within a RoleMessagesProvider');
  }
  return context;
}

